import React, { FC, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import MiddleLayout from 'src/layout/MiddleLayout';
import ModalErrorLayout from 'src/components/organisms/modal/ModalErrorLayout';
import InputLoginBox from 'src/components/atoms/input/InputLoginBox';
import InputConfirmBox from 'src/components/atoms/input/InputConfirmBox';
import DefaultButton from 'src/components/atoms/buttons/DefaultButton';
import { errorModalViewStore, loadingModalViewStore } from 'src/store/modal';
import { login } from 'src/fetch/member';
import { publicApi } from 'src/utils/auth';
import './style.scss';

// 로그인에 사용할 귀속연도와 회사코드
const LOGIN_ACCT_YEAR = '2024';
const LOGIN_CO_CD = '0001';

type Options = {
  cust_cd: string;
  cust_nm: string;
  default_tenant: boolean;
  companies: { acct_year: string; co_cd: string; co_nm: string }[];
};

const LoginTemplate: FC = () => {
  const loadingState = loadingModalViewStore((state: any) => state.set);
  const setErrorState = errorModalViewStore(state => state.set);
  const queryClient = useQueryClient();
  const [options, setOptions] = useState<Options | null>(null);
  const [manual, setManual] = useState(false);
  const [pending, setPending] = useState(true);
  const [message, setMessage] = useState('');
  const [form, setForm] = useState({ co_id: '', cust_cd: '' });
  const passwordInputRef = useRef<HTMLInputElement | null>(null);
  const submitting = useRef(false);
  const pathSchema = window.location.pathname.split('/').filter(Boolean)[0];
  const schema =
    new URLSearchParams(window.location.search).get('cust_schema') ||
    (pathSchema && !['login', 'main', 'error'].includes(pathSchema) ? pathSchema : 'public');

  useEffect(() => {
    let active = true;
    sessionStorage.setItem('cust_schema', schema);
    setPending(true);
    publicApi
      .post('/api/auth/login-options', { cust_schema: schema })
      .then(({ data }) => {
        if (!active) return;
        if (String(data.status?.code) !== '200')
          throw new Error(data.status?.message || '회사 목록을 불러오지 못했습니다.');
        const result: Options = data.data.result;
        setOptions(result);
      })
      .catch(error => {
        if (!active) return;
        if (error.response?.status === 403) setManual(true);
        setMessage(
          error.response?.status === 403
            ? '회사 목록 조회가 비활성화되어 있습니다. 고정된 귀속연도와 회사코드로 로그인합니다.'
            : error.response?.data?.status?.message ||
                error.message ||
                '회사 목록을 불러오지 못했습니다.'
        );
      })
      .finally(() => {
        if (active) setPending(false);
      });
    return () => {
      active = false;
    };
  }, [schema]);

  const submit = async () => {
    if (submitting.current || pending || (!options && !manual)) return;
    const { co_id } = form;
    const passwd = passwordInputRef.current?.value || '';
    if (!co_id.trim() || !passwd) {
      setMessage('사번과 비밀번호를 모두 입력해 주세요.');
      return;
    }
    if (manual && schema !== 'public' && !form.cust_cd.trim()) {
      setMessage('고객코드를 입력해 주세요.');
      return;
    }
    submitting.current = true;
    setPending(true);
    setMessage('');
    try {
      await login(
        {
          acct_year: LOGIN_ACCT_YEAR,
          co_cd: LOGIN_CO_CD,
          co_id: co_id.trim(),
          passwd,
          ...(options && !options.default_tenant ? { cust_cd: options.cust_cd } : {}),
          ...(manual && schema !== 'public' ? { cust_cd: form.cust_cd.trim() } : {}),
        },
        loadingState,
        setErrorState,
        queryClient
      );
    } finally {
      submitting.current = false;
      setPending(false);
    }
  };
  const btnClickEvt = () => {
    void submit();
  };

  const content = (
    <div className="_login_content_layout">
      <div className="_content_layout">
        <div className="_middle">
          <div className="_main">
            <div className="_title_layout">
              <div className="_text">로그인</div>
            </div>
          </div>
          <form
            className="_bottom worker-login"
            onSubmit={event => {
              event.preventDefault();
              void submit();
            }}
          >
            <fieldset disabled={pending}>
              {manual && schema !== 'public' && (
                <label>
                  고객코드
                  <input
                    required
                    value={form.cust_cd}
                    onChange={e => setForm({ ...form, cust_cd: e.target.value })}
                  />
                </label>
              )}
              <InputLoginBox
                styleDef={{ backgroundColor: '#fff' }}
                status=""
                placeholder="사번"
                value={form.co_id}
                changeEvt={event => setForm({ ...form, co_id: event.target.value })}
              />
              <InputConfirmBox
                inputType="password"
                placeHolder="비밀번호"
                iconType=""
                inpRef={passwordInputRef}
                btnClickEvt={() => {
                  void submit();
                }}
              />
              <p className="error_msg" role="status">
                {message}
              </p>
              <div className="_btn_div">
                <DefaultButton
                  text="Log In"
                  clickEvent={btnClickEvt}
                  styleDef={{ height: '40px' }}
                  type="type5"
                  status={pending || (!options && !manual) ? 'disable' : ''}
                />
              </div>
            </fieldset>
          </form>
        </div>
      </div>
    </div>
  );
  return (
    <div className="_container">
      <MiddleLayout middle={content} />
      <ModalErrorLayout />
    </div>
  );
};
export default LoginTemplate;
